/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';

/**
 * Part 3 - "<sea/ep> <episode title>"
 * @returns `JSX.Element`
 *
 * Used for episode related actions on Episode screen only:
 * "<sea/ep> <episode title>"
 *
 * Notes:
 * Line limit: 1
 * If there is no season or episode information, do not display "<sea/ep> ".
 * For "<episode title>" formatting and fallback if not available,
 * see <episode title> in Program Data.
 * Truncate "<episode title>" if necessary using Partial Word Truncation.
 * If genre is "sports event": Do not display "<sea/ep>".
 *
 * @usecase
 * Use Case:
 * To display episode-related information in the format:
 * It ensures proper formatting, truncation for episode titles.
 *
 * @param {Object} props - The props for the component.
 * @param {number} [props.seasonNumber] - The season number of the episode (optional).
 * @param {number} [props.episodeNumber] - The episode number of the episode (optional).
 * @param {string} [props.episodeTitle] - The title of the episode (optional).
 * @param {string} [props.showType] - The type of the show Movie or Series etc.
 *
 * @returns {JSX.Element} A JSX element that displays the formatted episode title.
 */
const SeaEpEpisodeTitle: FunctionComponent<{
    seasonNumber?: number;
    episodeNumber?: number;
    episodeTitle?: string;
    showType?: string;
    quoted?: boolean;
    trailingSeaEp?: boolean;
}> = ({ seasonNumber, episodeNumber, episodeTitle, quoted = true, trailingSeaEp = false }) => {
    const season = seasonNumber ? `S${seasonNumber}` : '';
    const episode = episodeNumber ? `E${episodeNumber}` : '';
    const seaEp = `${season} ${episode}`.trim();

    return (
        <React.Fragment>
            <div className="uikit primary-text-style text-size-xlarge">
                {seaEp ? (
                    <>
                        {!trailingSeaEp && <span>{seaEp}</span>}
                        {quoted && LEFT_QUOTE}
                        {episodeTitle}
                        {quoted && RIGHT_QUOTE}
                        {trailingSeaEp && <span className="text-light">{`, ${seaEp}`}</span>}
                    </>
                ) : (
                    <>
                        {quoted && LEFT_QUOTE}
                        {episodeTitle}
                        {quoted && RIGHT_QUOTE}
                    </>
                )}
            </div>
        </React.Fragment>
    );
};
export default SeaEpEpisodeTitle;
